import {
  getValueByClassification,
  normalizeFieldToArray,
  getCopyrightStatements
} from "@thegetty/linkedart.js";
import {
  getClearanceLevel,
  getVisualItemsRightsStatementId,
  getIIIFImageId,
  getAltText,
  getDescription,
  extractImageUuid
} from "@thegetty-private/linked-data.js";
import aat from "@/data/aat";
import internal from "@/data/internal-ontologies";
import rightsStatements from "@/data/rights-statements";

const DIGITALLY_SHOWN_BY = "digitally_shown_by";

/**
 * Checks whether an image supports the allowed permissions or not
 *
 * @param {object} object
 * @param {array} allowedImagePermissions
 *
 * @returns {boolean}
 */
function canImage(object, allowedImagePermissions) {
  if (object == undefined) {
    return false;
  }

  const clearanceLevels = getClearanceLevel(object);
  const clearanceIds = clearanceLevels.map((obj) => obj.id);
  const includesAllowed = clearanceIds.some((id) =>
    allowedImagePermissions.includes(id)
  );

  return includesAllowed;
}

/**
 * Checks the visual item clearance to determine if the image
 * can be zoomed
 *
 * @param {object} object - the JSON-LD VisualItem
 *
 * @returns {boolean} whether or not to display the image
 */
export function isZoomable(object) {
  const allowedImagePermissions = [
    internal.DOWNLOAD,
    internal.ENLARGE,
    internal.ZOOM
  ];
  return canImage(object, allowedImagePermissions);
}

/**
 * Checks the visual item clearance to determine if the image
 * can be displayed
 *
 * @param {object} object - the JSON-LD VisualItem
 *
 * @returns {boolean} whether or not to display the image
 */
export function isDisplayable(object) {
  const allowedImagePermissions = [
    internal.DOWNLOAD,
    internal.ENLARGE,
    internal.THUMBNAIL,
    internal.ZOOM
  ];

  return canImage(object, allowedImagePermissions);
}

/**
 * Whether the visual item can be downloaded or not
 *
 * @param {object} object - the Visual Item
 *
 * @returns {boolean}
 */
export function canDownload(object) {
  return canImage(object, [internal.DOWNLOAD]);
}

/**
 * Checks the dimensions of a visual item to determine if the portrait
 * should be used (includes square images)
 *
 * @param {object} object - the JSON-LD VisualItem
 *
 * @returns {boolean} whether or not to use portrait view
 */
export function isPortrait(object) {
  const digitallyShownBy = normalizeFieldToArray(object, DIGITALLY_SHOWN_BY);

  if (!digitallyShownBy) {
    return undefined;
  }

  // check for both dimensions & dimension field
  let dimensions = digitallyShownBy[0].dimensions;
  if (dimensions == undefined) {
    dimensions = digitallyShownBy[0].dimension;
  }

  const height = getValueByClassification(dimensions, aat.HEIGHT);
  const width = getValueByClassification(dimensions, aat.WIDTH);

  return width > height ? false : true;
}

/**
 * Extracts the IIIF Image UUID from the current canvas
 * @returns {string}
 */
export function getCanvasUuid(canvas) {
  if (canvas == undefined) {
    return;
  }
  return extractImageUuid(canvas.images[0].resource.service["@id"]);
}

/**
 * Extracts the IIIF Image service url from the selected canvas
 * @returns {string}
 */
export function getServiceUrl(canvas) {
  if (canvas == undefined) {
    return;
  }
  return canvas.images[0].resource.service["@id"];
}

/**
 * Extracts a visual item's copyright statement
 *
 * @param {object} object - the JSON-LD VisualItem
 *
 * @returns {string}
 */
export function getCopyright(object) {
  const copyrightStatements = getCopyrightStatements(object);
  if (copyrightStatements.length === 0) {
    return;
  }
  return copyrightStatements[0];
}

/**
 * Gets the RightsStatement.org ID from the Visual Item
 *
 * @param {Object} visualItem  - the Visual item
 *
 * @returns {String} the Rights Statement.org ID
 */
export function getRightsStatementId(visualItem) {
  let statement = getVisualItemsRightsStatementId(
    visualItem,
    internal.RIGHTS_STATEMENT_ID
  );

  if (statement == undefined) {
    statement = getVisualItemsRightsStatementId(visualItem);
  }

  return statement;
}

/**
 * Gets and formats metadata for an array of visual items
 * @param {array} visualItems - array of visual item lod objects
 * @returns {array} visual item metadata objects
 */
export function getVisualItemMetadata(visualItems) {
  return visualItems.map((item) => {
    let itemMetadata = {
      id: item.id,
      iiifImageId: getIIIFImageId(item),
      altText: getAltText(item),
      canDownload: canDownload(item),
      descriptiveNote: getDescription(item),
      disableContextMenu: isInCopyright(item),
      isDisplayable: isDisplayable(item),
      isPortrait: isPortrait(item),
      isZoomable: isZoomable(item),
      rightsStatementId: getRightsStatementId(item),
      copyright: getCopyright(item)
    };

    // remove empty values
    itemMetadata = Object.entries(itemMetadata).filter(
      (e) => e[1] !== undefined
    );
    return Object.fromEntries(itemMetadata);
  });
}

/**
 * Temporary helper to determine whether visual item is in copyright
 * @todo remove after image viewer launch
 *
 * @param {object} visualItem
 * @returns {boolean}
 */
export function isInCopyright(visualItem) {
  let rightsStatementId = getRightsStatementId(visualItem);
  if (rightsStatementId == undefined) {
    return true;
  }
  return (
    rightsStatementId.endsWith(rightsStatements.IN_COPYRIGHT) ||
    rightsStatementId.endsWith(rightsStatements.IN_COPYRIGHT_UNIDENTIFIABLE)
  );
}
